import { useCreateStationeryDraftMutation } from '../../Dashboard/components/ProductDetailsPage/ProductDetailsPage.graphql.generated';
import { StationeryDraftFormat } from '@graphql/generated-base';
import type { CardDraftCreatedFormat, CardDraftCreatedPageValue } from '../PublicShop.telemetry';
import { usePublicCardShopTelemetry } from '../PublicShop.telemetry';
import { useHistory } from '@shared/core';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useCallback } from 'react';
import type {
  CustomizeTemplateData,
  MinimalRequiredTemplateDataForDraft,
  ValidDigitalDesignApiData,
} from '@apps/card/routes/Dashboard/components/DesignsGallery/DesignsGallery.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { withWindow } from '@shared/utils/withWindow';
import { useRedirectAfterTimeout } from './useRedirectAfterTimeout';
import { addSuppressedError } from '@shared/utils/logger';
import { useConstructEcardEditorRedirectUrl } from '@apps/card/routes/Dashboard/components/DesignsGallery';

/**
 * Handle paper card customization redirects
 */
const useCreateCardDraftAndRedirectToEditor = (page: CardDraftCreatedPageValue) => {
  const { cardDraftCreated } = usePublicCardShopTelemetry();
  const { getCustomizeDraftPath, getDigitalCustomizeDraftPath } = useCardsRouterContext();
  const history = useHistory();

  const [callCreateDraft] = useCreateStationeryDraftMutation();

  // `telemetryFormat` exists because the draft's own `format` only distinguishes print
  // from digital, so it cannot tell paid premium digital apart from the free Basic
  // Digital V2. The result is handled here rather than in the mutation's `onCompleted`
  // so the requested customization format stays in closure scope -- shared mutable state
  // would mislabel the first draft if a second customize click landed before it resolved.
  const createCardDraftAndRedirectToEditor = useCallback(
    async (template: MinimalRequiredTemplateDataForDraft, draftFormat: StationeryDraftFormat, priceConfigId: string | undefined, telemetryFormat: CardDraftCreatedFormat) => {
      try {
        const response = await callCreateDraft({
          variables: {
            payload: {
              cardJSON: template.themeJSON,
              stationeryTemplateId: template.id,
              format: draftFormat,
              priceConfigId,
            },
          },
        });

        const draft = response.data?.createStationeryDraft;
        if (!draft) {
          return;
        }

        const { stationeryTemplate, id, format } = draft;
        cardDraftCreated({
          themeId: stationeryTemplate.themeId,
          stationeryTemplateCategory: stationeryTemplate.category,
          page,
          format: telemetryFormat,
          isDigital: format === 'digital',
          isPremiumDesign: stationeryTemplate.premium,
          cardWidthInches: stationeryTemplate.widthInches,
          cardHeightInches: stationeryTemplate.heightInches,
        });

        const paperRoute = '/event/edit' + getCustomizeDraftPath(id);
        const digitalRoute = '/event/edit' + getDigitalCustomizeDraftPath(id);

        history.push(format === 'digital' ? digitalRoute : paperRoute);
      } catch (error) {
        // No error UI on this path today -- previously the un-awaited mutation left an
        // unhandled rejection, so this at least records it. Same as useCustomizePaperCard.
        addSuppressedError(error, { context: 'useCreateDraftAndRedirectToEditor', themeId: template.themeId });
      }
    },
    [callCreateDraft, cardDraftCreated, getCustomizeDraftPath, getDigitalCustomizeDraftPath, history, page],
  );

  return { createCardDraftAndRedirectToEditor };
};

const useRedirectToEcardEditor = () => {
  const constructEcardEditorRedirectUrl = useConstructEcardEditorRedirectUrl();
  const redirectAfterTimeout = useRedirectAfterTimeout();

  const redirectToEcardEditor = useEventCallback((template: ValidDigitalDesignApiData) => {
    const redirectUrl = constructEcardEditorRedirectUrl(template.category, template.themeId);
    redirectAfterTimeout(redirectUrl);
  });

  return { redirectToEcardEditor };
};

export const useCreateDraftAndRedirectToEditor = (page: CardDraftCreatedPageValue) => {
  const { createCardDraftAndRedirectToEditor } = useCreateCardDraftAndRedirectToEditor(page);
  const { redirectToEcardEditor } = useRedirectToEcardEditor();
  const redirectAfterTimeout = useRedirectAfterTimeout();
  const { getConciergePath } = useCardsRouterContext();

  const createDraftAndRedirectToEditor = useEventCallback((data: CustomizeTemplateData & { priceConfigId?: string }) => {
    if (data.format === 'paper') {
      createCardDraftAndRedirectToEditor(data.template, StationeryDraftFormat.print, data.priceConfigId, 'paper');
    } else if (data.format === 'premiumDigital') {
      createCardDraftAndRedirectToEditor(data.template, StationeryDraftFormat.digital, data.priceConfigId, 'premiumDigital');
    } else if (data.format === 'basicDigitalV2') {
      // Same draft-backed digital flow as premium; the free product's own `priceConfigId`
      // is what distinguishes the draft, so only the telemetry format differs here.
      createCardDraftAndRedirectToEditor(data.template, StationeryDraftFormat.digital, data.priceConfigId, 'basicDigitalV2');
    } else if (data.format === 'concierge') {
      withWindow(global => {
        redirectAfterTimeout(global.location.origin + '/event/edit' + getConciergePath(data.template.identifier));
      });
    } else {
      redirectToEcardEditor(data.template);
    }
  });

  return { createDraftAndRedirectToEditor };
};
